import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAnalytics } from "../../context/AnalyticsContext";
import { LoadingState, ErrorState, SuccessState } from "../../components/ui/StatusStates";
import api from "../../services/api";

const SubscriptionSuccess = () => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { trackSubscriptionChange } = useAnalytics();
  
  // Get session_id from URL
  const sessionId = searchParams.get("session_id");
  
  // Verify checkout session when component mounts
  useEffect(() => {
    const verifySession = async () => {
      try {
        if (!currentUser) {
          setIsVerifying(false);
          setError("You must be logged in to verify your subscription.");
          return;
        }
        
        const response = await api.post("/subscription/verify-session", { sessionId });
        
        if (response.data.status === "success") {
          setIsVerified(true);
          // Track successful subscription upgrade
          trackSubscriptionChange('pro');
        } else {
          setError(response.data.message || "Unable to verify your subscription.");
        }
      } catch (err) {
        console.error("Error verifying subscription:", err);
        setError(err.response?.data?.message || "Failed to verify your subscription. Please try again later.");
      } finally {
        setIsVerifying(false);
      }
    };
    
    if (currentUser && sessionId) {
      verifySession();
    } else if (!sessionId) {
      setIsVerifying(false);
      setError("No session ID provided. Unable to verify your subscription.");
    }
  }, [sessionId, currentUser]);
  
  // Redirect to dashboard after 5 seconds on success
  useEffect(() => {
    let redirectTimer;
    if (isVerified && !isVerifying) {
      redirectTimer = setTimeout(() => {
        navigate("/dashboard");
      }, 5000);
    }
    
    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [isVerified, isVerifying, navigate]);
  
  return (
    <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="bg-light text-center">
                <h4 className="my-2">{isVerified ? "Subscription Successful!" : "Verifying Subscription"}</h4>
              </Card.Header>
              {isVerifying ? (
                <Card.Body>
                  <LoadingState 
                    message="Verifying your subscription..." 
                    size="medium"
                  />
                </Card.Body>
              ) : isVerified ? (
                <Card.Body className="p-0">  
                  <SuccessState
                    title="Thank you for upgrading to Pro!"
                    message="Your subscription has been activated successfully. You now have access to all Pro features and benefits."
                    size="large"
                  >
                    <Alert variant="success" className="mb-4">
                      <strong>Your Pro benefits are now active.</strong> You now have unlimited clicks 
                      and access to advanced features.
                    </Alert>
                    
                    <p>
                      You will be redirected to the dashboard in a few seconds...
                    </p>
                  </SuccessState>
                </Card.Body>
              ) : (
                <Card.Body className="p-0">  
                  <ErrorState
                    message={error || "We could not verify your subscription. Please try again or contact support."}
                    size="medium"
                  >
                    <div className="d-grid gap-2 mt-3">
                      <Link to="/dashboard" className="btn btn-primary">
                        Back to Dashboard
                      </Link>
                      <Link to="/dashboard/subscription" className="btn btn-outline-primary">
                        View Subscription Options
                      </Link>
                    </div>
                  </ErrorState>
                </Card.Body>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
  );
};

export default SubscriptionSuccess;
